import { Container, Graphics, Sprite, Text } from 'pixi.js';
import type { CurrencyFormat, LayoutProfile, ResolvedTheme, Strings } from '../types';
import type { AudioManager } from '../audio/audio';
import { alphaOf, toPixiColor } from '../util/color';
import { formatMoney, t as tr } from '../util/format';
import type { TextureStore } from './textures';
import { Easings, type Timeline } from './tween';

interface Coin {
  sprite: Sprite | Graphics;
  vx: number;
  vy: number;
  vr: number;
}

/** Full-screen presentations: big-win tiers, free-spin intro/outro/retrigger, persistent free-spin counter. */
export class Overlays extends Container {
  private theme: ResolvedTheme;
  private strings: Strings;
  private readonly currency: CurrencyFormat;
  private readonly textures: TextureStore;
  private readonly audio: AudioManager;
  private readonly backdrop = new Graphics();
  private readonly coins = new Container();
  private readonly panel = new Container();
  private readonly title: Text;
  private readonly subtitle: Text;
  private readonly amount: Text;
  private readonly hint: Text;
  private readonly toast: Text;
  private readonly counter = new Container();
  private readonly counterBg = new Graphics();
  private readonly counterText: Text;
  private W = 390;
  private H = 844;
  /** Full canvas rectangle in local (design) units, so backdrops and coins cover the whole screen. */
  private view = { x: 0, y: 0, w: 390, h: 844 };

  constructor(o: { theme: ResolvedTheme; strings: Strings; currency: CurrencyFormat; textures: TextureStore; audio: AudioManager }) {
    super();
    this.theme = o.theme;
    this.strings = o.strings;
    this.currency = o.currency;
    this.textures = o.textures;
    this.audio = o.audio;
    this.title = this.makeText(48, 0xffffff);
    this.subtitle = this.makeText(24, 0xffffff);
    this.amount = this.makeText(44, 0xffffff);
    this.hint = this.makeText(16, 0xffffff, false);
    this.toast = this.makeText(28, 0xffffff);
    this.counterText = this.makeText(16, 0xffffff, false);
    this.panel.addChild(this.title, this.subtitle, this.amount, this.hint);
    this.counter.addChild(this.counterBg, this.counterText);
    this.addChild(this.backdrop, this.coins, this.panel, this.toast, this.counter);
    this.backdrop.visible = false;
    this.panel.visible = false;
    this.toast.visible = false;
    this.counter.visible = false;
    this.eventMode = 'none';
  }

  private makeText(size: number, color: number, display = true): Text {
    const text = new Text({
      text: '',
      style: {
        fontFamily: display ? this.theme.fonts.display.family : this.theme.fonts.ui.family,
        fontSize: size,
        fontWeight: display ? '900' : '600',
        fill: color,
        stroke: display ? { color: 0x000000, width: Math.max(3, size * 0.1) } : undefined,
        dropShadow: display ? { alpha: 0.6, blur: 6, distance: 3, color: 0x000000 } : undefined,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: 900,
      },
    });
    text.anchor.set(0.5);
    return text;
  }

  layout(profile: LayoutProfile): void {
    this.W = profile.designWidth;
    this.H = profile.designHeight;
    const base = Math.min(this.W, this.H * 0.55);
    this.title.style.fontSize = base * 0.13;
    this.title.style.stroke = { color: 0x000000, width: base * 0.014 };
    this.subtitle.style.fontSize = base * 0.07;
    this.amount.style.fontSize = base * 0.12;
    this.amount.style.stroke = { color: 0x000000, width: base * 0.012 };
    this.hint.style.fontSize = base * 0.045;
    this.toast.style.fontSize = base * 0.075;
    this.counterText.style.fontSize = base * 0.045;
    for (const t of [this.title, this.subtitle, this.amount, this.hint, this.toast, this.counterText]) t.style.wordWrapWidth = this.W * 0.9;
    const cx = this.W / 2;
    const cy = this.H * 0.42;
    this.title.position.set(cx, cy - base * 0.18);
    this.subtitle.position.set(cx, cy - base * 0.05);
    this.amount.position.set(cx, cy + base * 0.08);
    this.hint.position.set(cx, cy + base * 0.22);
    this.toast.position.set(cx, this.H * 0.12);
    this.counter.position.set(cx, Math.max(24, profile.reels.y - base * 0.085));
    this.drawBackdrop();
    this.drawCounter();
  }

  /** Called by the presenter on resize with the visible canvas rect in design units. */
  setViewport(view: { x: number; y: number; w: number; h: number }): void {
    this.view = view;
    this.drawBackdrop();
  }

  private drawBackdrop(): void {
    const c = this.theme.palette.overlayBackdrop;
    const v = this.view;
    this.backdrop.clear().rect(v.x, v.y, v.w, v.h).fill({ color: toPixiColor(c), alpha: alphaOf(c) });
  }

  private drawCounter(): void {
    const w = Math.min(this.W * 0.9, this.counterText.width + 40);
    const h = this.counterText.height + 16;
    this.counterBg
      .clear()
      .roundRect(-w / 2, -h / 2, w, h, h / 2)
      .fill({ color: toPixiColor(this.theme.palette.hudBackground), alpha: alphaOf(this.theme.palette.hudBackground) })
      .roundRect(-w / 2, -h / 2, w, h, h / 2)
      .stroke({ color: toPixiColor(this.theme.palette.accent), width: 2 });
  }

  setTheme(theme: ResolvedTheme): void {
    this.theme = theme;
    for (const t of [this.title, this.subtitle, this.amount, this.toast]) t.style.fontFamily = theme.fonts.display.family;
    for (const t of [this.hint, this.counterText]) t.style.fontFamily = theme.fonts.ui.family;
    this.drawBackdrop();
    this.drawCounter();
  }

  setStrings(strings: Strings): void {
    this.strings = strings;
  }

  // ------------------------------------------------------------------ helpers

  private async fadeIn(timeline: Timeline, ms = 300): Promise<void> {
    this.backdrop.visible = true;
    this.panel.visible = true;
    this.backdrop.alpha = 0;
    this.panel.alpha = 0;
    this.panel.scale.set(0.6);
    await timeline.tween({
      duration: ms,
      easing: Easings.backOut,
      update: (t) => {
        this.backdrop.alpha = Math.min(1, t);
        this.panel.alpha = Math.min(1, t);
        this.panel.scale.set(0.6 + 0.4 * t);
      },
    });
    this.panel.scale.set(1);
    this.panel.alpha = 1;
    this.backdrop.alpha = 1;
  }

  private async fadeOut(timeline: Timeline, ms = 250): Promise<void> {
    await timeline.tween({
      duration: ms,
      update: (t) => {
        this.backdrop.alpha = 1 - t;
        this.panel.alpha = 1 - t;
      },
    });
    this.backdrop.visible = false;
    this.panel.visible = false;
    this.backdrop.alpha = 1;
    this.panel.alpha = 1;
  }

  private setPanel(title: string, subtitle: string, amount: string, hint: string, color: number): void {
    this.title.text = title;
    this.title.style.fill = color;
    this.subtitle.text = subtitle;
    this.amount.text = amount;
    this.amount.style.fill = color;
    this.hint.text = hint;
    this.hint.style.fill = toPixiColor(this.theme.palette.textMuted);
    this.panel.pivot.set(this.W / 2, this.H * 0.42);
    this.panel.position.set(this.W / 2, this.H * 0.42);
  }

  private startCoins(timeline: Timeline, count: number): () => void {
    const tex = this.theme.assets.coin ? this.textures.image(this.theme.assets.coin) : null;
    const coins: Coin[] = [];
    const size = Math.max(18, this.W * 0.06);
    const gold = toPixiColor(this.theme.palette.winBig);
    for (let i = 0; i < count; i++) {
      let sprite: Sprite | Graphics;
      if (tex) {
        const sp = new Sprite(tex);
        sp.anchor.set(0.5);
        sp.width = size;
        sp.height = size;
        sprite = sp;
      } else {
        // No coin asset: draw a simple disc in the win colour.
        sprite = new Graphics()
          .circle(0, 0, size / 2)
          .fill({ color: gold })
          .circle(0, 0, size / 2)
          .stroke({ color: 0x000000, alpha: 0.3, width: Math.max(1, size * 0.08) });
      }
      sprite.position.set(this.view.x + Math.random() * this.view.w, this.view.y - size - Math.random() * this.view.h);
      this.coins.addChild(sprite);
      coins.push({ sprite, vx: (Math.random() - 0.5) * 60, vy: 120 + Math.random() * 260, vr: (Math.random() - 0.5) * 6 });
    }
    this.coins.visible = true;
    const stop = timeline.loop({
      period: 1000,
      update: (_p, dt) => {
        const s = dt / 1000;
        for (const c of coins) {
          c.vy += 500 * s;
          c.sprite.x += c.vx * s;
          c.sprite.y += c.vy * s;
          c.sprite.rotation += c.vr * s;
          c.sprite.scale.x = (c.sprite.scale.y || 1) * (0.55 + 0.45 * Math.abs(Math.cos(c.sprite.rotation * 2)));
          if (c.sprite.y > this.view.y + this.view.h + size) {
            c.sprite.y = this.view.y - size;
            c.sprite.x = this.view.x + Math.random() * this.view.w;
            c.vy = 120 + Math.random() * 200;
          }
        }
      },
    });
    return () => {
      stop();
      this.coins.removeChildren().forEach((c) => c.destroy());
    };
  }

  // ------------------------------------------------------------------ presentations

  async bigWin(tier: 'big' | 'mega' | 'epic', amount: number, timeline: Timeline): Promise<void> {
    const T = this.theme.animations.timing.win;
    const p = this.theme.palette;
    const color = toPixiColor(tier === 'epic' ? p.winEpic : tier === 'mega' ? p.winMega : p.winBig);
    const label = tr(this.strings, tier === 'epic' ? 'epicWin' : tier === 'mega' ? 'megaWin' : 'bigWin');
    this.setPanel(label, '', formatMoney(0, this.currency), tr(this.strings, 'skip'), color);
    this.audio.play(tier === 'epic' ? 'winEpic' : tier === 'mega' ? 'winMega' : 'winBig');
    const stopCoins = this.theme.animations.effects.winOverlay.coinShower ? this.startCoins(timeline, this.theme.animations.effects.winOverlay.coinCount) : () => {};
    await this.fadeIn(timeline, 400);
    const rollup = this.audio.play('rollup');
    await Promise.all([
      timeline.tween({
        duration: T.rollupBigMs,
        easing: Easings.quadOut,
        update: (t) => {
          this.amount.text = formatMoney(amount * t, this.currency);
          this.amount.scale.set(1 + 0.06 * Math.sin(t * Math.PI * 6));
        },
      }),
      timeline.wait(T.overlayMinMs),
    ]);
    rollup.stop();
    this.amount.text = formatMoney(amount, this.currency);
    this.amount.scale.set(1);
    this.audio.play('rollupEnd');
    await timeline.wait(400);
    stopCoins();
    await this.fadeOut(timeline);
  }

  async freeSpinsIntro(spins: number, timeline: Timeline): Promise<void> {
    const T = this.theme.animations.timing.freeSpins;
    const color = toPixiColor(this.theme.palette.winMega);
    this.setPanel(tr(this.strings, 'freeSpins'), tr(this.strings, 'congratulations'), tr(this.strings, 'freeSpinsAwarded', { n: spins }), tr(this.strings, 'continue'), color);
    this.amount.style.fill = toPixiColor(this.theme.palette.text);
    this.audio.play('freeSpinsIntro');
    await this.fadeIn(timeline, 450);
    await timeline.wait(T.introMs);
    await this.fadeOut(timeline);
  }

  async freeSpinsRetrigger(added: number, remaining: number, timeline: Timeline): Promise<void> {
    const T = this.theme.animations.timing.freeSpins;
    this.toast.text = `${tr(this.strings, 'retrigger', { n: added })}  (${remaining})`;
    this.toast.style.fill = toPixiColor(this.theme.palette.winMega);
    this.toast.visible = true;
    this.toast.alpha = 0;
    this.toast.scale.set(0.5);
    this.audio.play('retrigger');
    await timeline.tween({
      duration: 350,
      easing: Easings.backOut,
      update: (t) => {
        this.toast.alpha = Math.min(1, t);
        this.toast.scale.set(0.5 + 0.5 * t);
      },
    });
    this.toast.scale.set(1);
    await timeline.wait(T.retriggerToastMs);
    await timeline.tween({ duration: 250, update: (t) => (this.toast.alpha = 1 - t) });
    this.toast.visible = false;
  }

  async freeSpinsOutro(totalWin: number, timeline: Timeline): Promise<void> {
    const T = this.theme.animations.timing.freeSpins;
    const color = toPixiColor(this.theme.palette.winBig);
    this.setPanel(tr(this.strings, 'totalWin'), tr(this.strings, 'freeSpins'), formatMoney(0, this.currency), tr(this.strings, 'continue'), color);
    this.audio.play('freeSpinsOutro');
    const stopCoins = totalWin > 0 && this.theme.animations.effects.winOverlay.coinShower ? this.startCoins(timeline, Math.round(this.theme.animations.effects.winOverlay.coinCount / 2)) : () => {};
    await this.fadeIn(timeline, 400);
    await timeline.tween({
      duration: Math.min(1500, T.outroMs * 0.5),
      easing: Easings.quadOut,
      update: (t) => (this.amount.text = formatMoney(totalWin * t, this.currency)),
    });
    this.amount.text = formatMoney(totalWin, this.currency);
    await timeline.wait(T.outroMs * 0.5);
    stopCoins();
    await this.fadeOut(timeline);
  }

  freeSpinsCounter(remaining: number | null, totalWin: number): void {
    if (remaining == null) {
      this.counter.visible = false;
      return;
    }
    this.counterText.text = `${tr(this.strings, 'freeSpinsLeft')}: ${remaining}   ·   ${tr(this.strings, 'totalWin')}: ${formatMoney(totalWin, this.currency)}`;
    this.counterText.style.fill = toPixiColor(this.theme.palette.text);
    this.drawCounter();
    this.counter.visible = true;
  }

  /** Hide everything instantly (skip / destroy). */
  reset(): void {
    this.backdrop.visible = false;
    this.panel.visible = false;
    this.toast.visible = false;
    this.coins.removeChildren().forEach((c) => c.destroy());
  }
}
